// src/pages/InvitePage.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInviteToken } from '../lib/supabase';
import { signUp } from '../lib/supabase';

const C = {
  bg:'#13171d', card:'#1e2430', border:'#2c3545', tag:'#1e2d3d',
  orange:'#00c8b0', orangeLt:'#20e0c8',
  text:'#e8f0f8', muted:'#8a9ab0', dim:'#4a5a70', cream:'#c0d8f0',
  green:'#00e880', red:'#ff4860', gold:'#f5c842',
};

function Inp({ label, type = 'text', value, onChange, placeholder, error }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>{label}</div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', background: C.tag, border: '1px solid ' + (error ? C.red : C.border), borderRadius: 8, padding: '10px 12px', color: C.text, fontFamily: "'Outfit',sans-serif", fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
      />
      {error && <div style={{ fontSize: 11, color: C.red, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

export default function InvitePage() {
  const { token } = useParams();
  const navigate  = useNavigate();
  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [invalid, setInvalid]     = useState(false);

  // Sign-up form state
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [errors,   setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getInviteToken(token)
      .then(d => {
        if (!d || d.used_by) { setInvalid(true); }
        else { setTokenData(d); }
        setLoading(false);
      })
      .catch(() => { setInvalid(true); setLoading(false); });
  }, [token]);

  const validate = () => {
    const e = {};
    if (!name.trim())               e.name     = 'Entrez votre nom';
    if (!email.includes('@'))       e.email    = 'Adresse e-mail invalide';
    if (password.length < 8)        e.password = 'Minimum 8 caractères';
    if (password !== confirm)       e.confirm  = 'Les mots de passe ne correspondent pas';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSubmitting(true);
    try {
      await signUp(email, password, name);
      sessionStorage.setItem('invite_token', JSON.stringify({
        token,
        type: tokenData.profile_type,
        agentName: tokenData.agent_name,
      }));
      navigate('/onboard');
    } catch (err) {
      setErrors({ submit: err.message });
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 24, height: 24, border: '2px solid ' + C.border, borderTop: '2px solid ' + C.orange, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (invalid) return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24 }}>
      <div style={{ fontSize: 48 }}>🔗</div>
      <div style={{ fontSize: 18, color: C.text, fontFamily: "'Cormorant Garamond',serif", fontWeight: 700 }}>Lien invalide ou déjà utilisé</div>
      <div style={{ color: C.muted, fontSize: 13 }}>Ce lien d'invitation n'est plus actif. Contactez votre agent.</div>
      <button onClick={() => navigate('/auth')} style={{ marginTop: 8, background: 'linear-gradient(135deg,'+C.orange+','+C.orangeLt+')', border: 'none', borderRadius: 10, color: '#fff', padding: '10px 24px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>
        Aller à la connexion
      </button>
    </div>
  );

  const typeLabel = tokenData.profile_type === 'artist' ? 'Artiste' : 'Lieu / Venue';
  const typeIcon  = tokenData.profile_type === 'artist' ? '🎵' : '🏛️';

  return (
    <div style={{ background: C.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'Outfit',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@700&family=Outfit:wght@300;400;600;700&display=swap'); input::placeholder{color:#4a5a70;}`}</style>
      <div style={{ width: '100%', maxWidth: 440, background: C.card, border: '1px solid ' + C.border, borderRadius: 20, padding: 36, boxShadow: '0 32px 80px #00000080' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/logo.svg" alt="StageMap" style={{ height: 32, display: 'block', margin: '0 auto 18px' }} />
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: C.cream, marginBottom: 6 }}>
            Invitation de {tokenData.agent_name || 'votre agent'}
          </div>
          <div style={{ color: C.muted, fontSize: 13 }}>
            Créez votre compte pour rejoindre StageMap
          </div>
        </div>

        {/* Locked profile type badge */}
        <div style={{ background: C.tag, border: '1px solid ' + C.border, borderRadius: 12, padding: '12px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 26 }}>{typeIcon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: C.dim, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Type de profil</div>
            <div style={{ fontSize: 14, color: C.text, fontWeight: 600 }}>{typeLabel}</div>
          </div>
          <span style={{ fontSize: 10, color: C.dim, background: C.border, borderRadius: 6, padding: '3px 8px' }}>Verrouillé</span>
        </div>

        {/* Sign-up form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Inp label="Votre nom" value={name} onChange={setName} placeholder="Nom complet ou nom d'artiste" error={errors.name} />
          <Inp label="Adresse e-mail" type="email" value={email} onChange={setEmail} placeholder="vous@exemple.com" error={errors.email} />
          <Inp label="Mot de passe" type="password" value={password} onChange={setPassword} placeholder="Minimum 8 caractères" error={errors.password} />
          <Inp label="Confirmer le mot de passe" type="password" value={confirm} onChange={setConfirm} placeholder="Répétez le mot de passe" error={errors.confirm} />

          {errors.submit && (
            <div style={{ background: C.red + '18', border: '1px solid ' + C.red + '44', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: C.red }}>
              {errors.submit}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{ background: submitting ? C.border : 'linear-gradient(135deg,'+C.orange+','+C.orangeLt+')', border: 'none', borderRadius: 12, color: '#fff', padding: '14px 0', fontSize: 14, fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: "'Outfit',sans-serif", width: '100%', marginTop: 4 }}>
            {submitting ? '⏳ Création du compte…' : '✨ Créer mon compte'}
          </button>
        </div>

        <div style={{ marginTop: 18, textAlign: 'center', fontSize: 11, color: C.dim }}>
          Ce lien expire le {new Date(tokenData.expires_at).toLocaleDateString('fr', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      </div>
    </div>
  );
}
